import React, { useRef, useState } from 'react';
import { Button } from "@/components/ui/button";
import { Loader2, RotateCcw, Send, Eye } from "lucide-react";
import { toast } from "sonner";
import { base44 } from "@/api/base44Client";
import TemplateFields from '@/components/TemplateFields';

/**
 * One of a show's four emails: the subject/body boxes (plus the text message
 * box, for Initial only), a live preview against a fabricated example
 * contact, a "Reset to industry default" button, and a "Send test to me"
 * button. Task 3.6 follow up.
 *
 * `value` is `{ sms, subject, body }`, the same neutral shape TemplateFields
 * already uses. The parent (Templates.jsx) is the one that knows which show
 * columns each of the four maps onto; this component only knows its own
 * `kind`, which it hands back to the API for rendering and sending.
 *
 * previewRepId and show (task: fix the Per-Event Override preview, 2026-09-24):
 * this editor is the Per-Event Override panel specifically, so unlike the
 * Default and Per-Rep panels it has both a real rep (whichever one is
 * selected in Per-Rep Override, or the logged in admin) and a real show
 * already in scope. Both are threaded down from Templates.jsx and passed to
 * every render call this component makes: its own email preview, its own
 * "Send test to me" (which already sends as the real logged in admin, so
 * only the show name needed adding there), and the embedded TemplateFields'
 * own text message preview on the Initial tab, so all of this screen's
 * preview buttons behave consistently instead of two of the three fixed and
 * a third still showing SAMPLE_REP/SAMPLE_SHOW's fake data.
 */
export default function EventEmailEditor({ kind, label, value, onChange, disabled = false, defaults, resolved, overrideLabel, previewRepId, show }) {
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null); // { subject, html } | null
  const [sendingTest, setSendingTest] = useState(false);
  const iframeRef = useRef(null);
  const [iframeHeight, setIframeHeight] = useState(0);

  const showSms = kind === 'initial';

  const handleReset = () => {
    if (!defaults) return;
    onChange({ ...value, subject: defaults.subject, body: defaults.body });
    setPreview(null);
    toast.success(`${label} reset to the industry default. Save Event to keep it.`);
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const rendered = await base44.reminders.previewEmail(kind, value.subject || '', value.body || '', previewRepId, show);
      setIframeHeight(0);
      setPreview(rendered);
    } catch (err) {
      toast.error(err.message || 'Could not render a preview');
    } finally {
      setPreviewing(false);
    }
  };

  const handleTestSend = async () => {
    setSendingTest(true);
    try {
      const result = await base44.reminders.testSendEmail(kind, value.subject || '', value.body || '');
      toast.success(`Test sent to ${result.to}`);
    } catch (err) {
      toast.error(err.message || 'Could not send the test');
    } finally {
      setSendingTest(false);
    }
  };

  // No internal scrollbar (task: auto-size the preview iframe, 2026-09-24):
  // measures the rendered document's own height once the srcDoc finishes
  // loading and sets the iframe to exactly that, so the whole email is
  // visible at once and the page itself scrolls, not a box inside it.
  // Reading contentWindow.document across a sandbox boundary needs
  // allow-same-origin; scripts stay fully blocked either way (allow-scripts
  // is not, and never was, in this sandbox list), which is the actual
  // protection a stray <script> in the admin's own not-yet-saved HTML needs.
  const handleIframeLoad = () => {
    const doc = iframeRef.current?.contentWindow?.document;
    const height = doc?.documentElement?.scrollHeight || doc?.body?.scrollHeight || 0;
    if (height) setIframeHeight(height);
  };

  return (
    <div className="space-y-3">
      <TemplateFields
        idPrefix={`event-${kind}`}
        value={value}
        onChange={onChange}
        disabled={disabled}
        showSms={showSms}
        resolved={resolved}
        overrideLabel={overrideLabel}
        previewRepId={previewRepId}
        previewShow={show}
      />

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleReset}
          disabled={disabled || !defaults}
          className="flex-1 min-h-8 text-xs"
        >
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
          Reset to industry default
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handlePreview}
          disabled={disabled || previewing}
          className="flex-1 min-h-8 text-xs"
        >
          {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Eye className="w-3.5 h-3.5 mr-1.5" />Preview email</>}
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleTestSend}
          disabled={disabled || sendingTest}
          className="flex-1 min-h-8 text-xs bg-accent hover:bg-accent/90 text-accent-foreground"
        >
          {sendingTest ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1.5" />Send test to me</>}
        </Button>
      </div>

      {preview && (
        <div className="rounded-xl border border-border overflow-hidden">
          <div className="bg-secondary/40 px-3 py-2 border-b border-border">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Subject</p>
            <p className="text-sm font-medium text-foreground">{preview.subject}</p>
          </div>
          {/* sandboxed: this is the admin's own typed HTML, rendered back to
              them, and the sandbox keeps a stray <script> inert the same way
              a real inbox would neutralize it. allow-same-origin only lets
              handleIframeLoad above read the rendered document's own height
              to auto-size this box; it does not permit script execution,
              which allow-scripts (not present here) would be the one
              actually controlling. */}
          <iframe
            ref={iframeRef}
            title={`${label} preview`}
            sandbox="allow-same-origin"
            srcDoc={preview.html}
            onLoad={handleIframeLoad}
            style={{ height: iframeHeight ? `${iframeHeight}px` : '18rem' }}
            className="w-full block bg-white"
          />
          <p className="text-[10px] text-muted-foreground px-3 py-1.5 bg-secondary/20">
            {show
              ? `Rendered against a fabricated example contact (Jane Smith) at ${show.name}. Nothing was sent.`
              : 'Rendered against a fabricated example contact (Jane Smith, Example Trade Show). Nothing was sent.'}
          </p>
        </div>
      )}
    </div>
  );
}
